import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ShieldCheck, CreditCard, MessageCircle, ArrowRight, Monitor, MapPin, Clock, Navigation, Cpu, Package, Check, Sparkles } from 'lucide-react';
import { BUSINESS, HERO_IMAGES, getFeaturedProducts } from '../config';
import ProductCard from '../components/ProductCard';

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    let active = true;
    getFeaturedProducts(8)
      .then((d) => { if (active) setFeatured(d?.products || []); })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  return (
    <div data-testid="home-page">
      {/* Le JSON-LD du magasin (@type ComputerStore, @id #localbusiness) vit dans public/index.html :
          un seul bloc par site (revue SEO/GEO du 24/10/2026 : deux entités décrivaient le même
          magasin, avec des téléphones de formats différents). */}

      {/* ─── HERO (fond sombre avec image atelier) ─── */}
      <section className="relative bg-primary text-white py-10 md:py-16 overflow-hidden" data-testid="hero-section">
        {/* Image atelier en background avec overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-45"
          style={{ backgroundImage: `url('${HERO_IMAGES.workshop}')` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/70 to-primary/20" aria-hidden="true" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 border-l-2 border-secondary pl-3 py-1 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                <span className="font-accent text-xs uppercase tracking-widest text-secondary">Boutique en ligne — Meaux 77100</span>
              </div>

              <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight leading-[1.05]">
                PC neufs <span className="text-secondary">&amp; reconditionnés</span><br />
                à Meaux
              </h1>

              <p className="mt-5 text-base md:text-lg text-slate-300 max-w-xl leading-relaxed">
                Des ordinateurs fiables, garantis, à prix juste.<br />
                Conseil personnalisé et retrait en boutique.
              </p>

              <div className="flex flex-wrap items-center gap-3 mt-8">
                <Link
                  to="/catalogue"
                  className="inline-flex items-center gap-2 bg-white text-primary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider py-3 px-6 rounded-sm text-sm transition-all active:scale-95 border-2 border-secondary"
                  data-testid="hero-catalogue-btn"
                >
                  Voir le catalogue
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={`tel:${BUSINESS.telephoneRaw}`}
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold uppercase tracking-wider py-3 px-5 rounded-sm text-sm transition-colors active:scale-95"
                  data-testid="hero-call-btn"
                >
                  <Phone className="w-4 h-4" />
                  Appeler la boutique
                </a>
              </div>

              {/* Puces stats hero retirées (boss 2026-06-02) : redondantes avec les
                  2 encarts de droite (Reconditionnés / Neufs). Ne pas réintroduire. */}
            </div>

            {/* Stack visuelle à droite — 2 encarts DISTINCTS (boss 2026-06-02) :
                page mixte neuf+recondi → 1 encart par univers, pour ne plus donner
                l'impression que tout est reconditionné. Wording Ads-safe (pas de
                « réparation/SAV »). */}
            <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
              {/* Encart Reconditionnés */}
              <div className="bg-secondary/10 border border-secondary/30 rounded-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-secondary shrink-0" />
                  <h3 className="font-heading text-base uppercase tracking-wider text-white">Reconditionnés en atelier</h3>
                </div>
                <ul className="space-y-1.5 text-xs text-slate-300 leading-relaxed">
                  <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-secondary shrink-0 mt-0.5" />Tests complets : SSD 100&nbsp;% santé, RAM zéro erreur, Windows vérifié</li>
                  <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-secondary shrink-0 mt-0.5" />6 mois pièces et main-d'œuvre, en plus de la garantie légale de 2&nbsp;ans</li>
                  <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-secondary shrink-0 mt-0.5" />Certificat PDF détaillé livré avec chaque PC</li>
                </ul>
              </div>
              {/* Encart Neufs (centrale d'achat) */}
              <div className="bg-white/5 border border-white/15 rounded-sm p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Cpu className="w-5 h-5 text-secondary shrink-0" />
                  <h3 className="font-heading text-base uppercase tracking-wider text-white">Neufs au meilleur prix</h3>
                </div>
                <ul className="space-y-1.5 text-xs text-slate-300 leading-relaxed">
                  <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-secondary shrink-0 mt-0.5" />Centrale d'achat : tarifs négociés au plus juste</li>
                  <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-secondary shrink-0 mt-0.5" />Garantie 2 ans (garantie légale)</li>
                  <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-secondary shrink-0 mt-0.5" />Préparation et mise en service à l'atelier, en option</li>
                  <li className="flex items-start gap-2"><Check className="w-3.5 h-3.5 text-secondary shrink-0 mt-0.5" />Conseil personnalisé pour bien choisir</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SÉLECTION METERIS (mise en avant — affichée seulement si featured) ─── */}
      {featured.length > 0 && (
        <section className="bg-white py-10 md:py-14 border-b border-slate-200" data-testid="featured-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-8">
              <span className="font-accent text-xs uppercase tracking-widest text-secondary">Sélection du moment</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-primary mt-2">Notre sélection METERIS</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {featured.slice(0, 4).map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── TRUST SIGNALS (fond clair) ─── */}
      <section className="bg-slate-50 py-10 md:py-14" data-testid="trust-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 md:mb-10">
            <span className="font-accent text-xs uppercase tracking-widest text-secondary">Nos engagements</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-primary mt-2">Ce qui change avec notre boutique</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: ShieldCheck, title: 'Garantie incluse', desc: 'Garantie légale de 2 ans, neuf comme reconditionné. Échange en boutique, pièces et main-d\'œuvre.' },
              { icon: MessageCircle, title: 'Conseils simples', desc: 'On vous aide à choisir le PC adapté à votre usage. Pas de jargon, que du concret.' },
              { icon: CreditCard, title: 'Paiement sur place', desc: 'CB, espèces, chèque. Pas de paiement en ligne, pas de frais cachés.' },
            ].map((item, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-sm p-6 hover:border-secondary transition-colors">
                <div className="w-12 h-12 rounded-sm bg-secondary/10 text-secondary flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-lg uppercase tracking-wider text-primary mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── NOTRE SÉLECTION (fond blanc) ─── */}
      <section className="bg-white py-10 md:py-14 border-t border-slate-200" data-testid="categories-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8 md:mb-10">
            <span className="font-accent text-xs uppercase tracking-widest text-secondary">Le catalogue</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight text-primary mt-2">Notre sélection</h2>
            <p className="text-slate-600 mt-3 max-w-xl mx-auto">Du matériel vérifié, testé et garanti.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { icon: Cpu, title: 'PC neufs', desc: 'Ordinateurs neufs sous emballage, avec garantie constructeur. Sélection éprouvée.', link: '/catalogue?condition=neuf' },
              { icon: Monitor, title: 'PC reconditionnés', desc: 'Portables et bureaux d\'occasion, vérifiés et garantis dans notre atelier. Dès 199 €.', link: '/catalogue?condition=reconditionne' },
              { icon: Package, title: 'Deals groupés', desc: 'Achetez ensemble, payez moins. Rejoignez un deal pour des prix dégressifs.', link: '/deals', comingSoon: true },
            ].map((cat, i) => {
              const cardBody = (
                <>
                  <div className="w-12 h-12 rounded-sm bg-secondary/10 text-secondary flex items-center justify-center mb-4">
                    <cat.icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading text-lg uppercase tracking-wider text-primary mb-2 group-hover:text-secondary transition-colors">{cat.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4 flex-1">{cat.desc}</p>
                  {cat.comingSoon ? (
                    <span className="font-heading text-xs font-semibold uppercase tracking-wider text-slate-400 inline-flex items-center gap-1">
                      Bientôt disponible
                    </span>
                  ) : (
                    <span className="font-heading text-xs font-semibold uppercase tracking-wider text-secondary inline-flex items-center gap-1">
                      Découvrir
                      <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  )}
                </>
              );
              // Deal teaser : carte visible avec ses effets graphiques mais NON cliquable
              // (feature pas encore opérationnelle). Badge « À venir » + léger grisé.
              if (cat.comingSoon) {
                return (
                  <div
                    key={i}
                    aria-disabled="true"
                    className="group relative flex flex-col bg-slate-50 border border-slate-200 rounded-sm p-6 opacity-80 cursor-default select-none hover:border-secondary hover:shadow-xl hover:-translate-y-0.5 transition-all"
                  >
                    <span className="absolute top-3 right-3 font-accent text-[10px] font-bold uppercase tracking-widest bg-slate-200 text-slate-500 px-2 py-0.5 rounded-sm">
                      À venir
                    </span>
                    {cardBody}
                  </div>
                );
              }
              return (
                <Link
                  key={i}
                  to={cat.link}
                  className="group flex flex-col bg-slate-50 border border-slate-200 rounded-sm p-6 hover:border-secondary hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  {cardBody}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section "Avis clients" volontairement retirée : tant qu'on n'a pas de
          fiche GMB dédiée à la boutique avec de vrais avis, on évite les faux
          avis (sanctions Google + détection facile par les utilisateurs).
          À remettre quand la fiche dédiée boutique aura 5-10 avis authentiques. */}

      {/* ─── VENEZ NOUS VOIR (fond sombre) ─── */}
      <section className="bg-primary text-white py-10 md:py-14" data-testid="contact-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
            <div>
              <span className="font-accent text-xs uppercase tracking-widest text-secondary">La boutique</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight mt-2 mb-6">Venez nous voir</h2>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-secondary mt-0.5 shrink-0" />
                  <div>
                    <p className="font-heading text-base uppercase tracking-wide text-white">{BUSINESS.adresse}</p>
                    {BUSINESS.urlGoogleMaps && (
                      <a href={BUSINESS.urlGoogleMaps} target="_blank" rel="noopener noreferrer" className="font-accent text-xs uppercase tracking-widest text-secondary hover:text-white transition-colors">
                        Voir sur Google Maps →
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-secondary mt-0.5 shrink-0" />
                  <div>
                    <a href={`tel:${BUSINESS.telephoneRaw}`} className="font-heading text-xl uppercase tracking-wide text-white hover:text-secondary transition-colors">{BUSINESS.telephone}</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-secondary mt-0.5 shrink-0" />
                  <div>
                    <p className="font-accent text-[10px] uppercase tracking-widest text-slate-400 mb-1">Retrait matériel</p>
                    <p className="font-heading text-base uppercase tracking-wide text-white">{BUSINESS.creneauRetrait}</p>
                  </div>
                </div>
              </div>

              <a
                href={BUSINESS.urlGoogleMaps}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-8 bg-white text-primary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider py-3 px-5 rounded-sm text-sm transition-colors border-2 border-secondary"
              >
                <Navigation className="w-4 h-4" />
                Itinéraire
              </a>
            </div>

            <div>
              <span className="font-accent text-xs uppercase tracking-widest text-secondary">Horaires d'ouverture</span>
              <h3 className="font-heading font-bold text-2xl uppercase tracking-tight mt-2 mb-6">Quand venir</h3>
              <div className="space-y-2">
                {BUSINESS.horaires && Object.entries(BUSINESS.horaires).map(([day, hours]) => (
                  <div key={day} className="flex justify-between items-baseline py-2 border-b border-white/10">
                    <span className="font-accent text-sm uppercase tracking-wider text-slate-400">{day}</span>
                    <span className={hours === 'Fermé' ? 'font-heading text-sm uppercase tracking-wider text-slate-500' : 'font-heading text-sm uppercase tracking-wider text-white'}>
                      {hours}
                      {BUSINESS.horairesLiens?.[day] && (
                        <>
                          {' — '}
                          <a
                            href={BUSINESS.horairesLiens[day].url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-secondary hover:underline"
                          >
                            {BUSINESS.horairesLiens[day].texte}
                          </a>
                        </>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
